<template>
  <v-dialog v-model="showDialog" v-if="currentProject" max-width="800px">
    <v-card id="charts-dialog" class="disablehotkeys" style="overflow:visible;">
      <v-card-title>
        <span class="headline">{{ currentProject.name || 'Project ' + currentProject.id }}</span>
        <v-spacer></v-spacer>
        <v-btn @click="showDialog=false" icon><v-icon>close</v-icon></v-btn>
      </v-card-title>
      <v-card-text class="pt-0">
        <v-container grid-list-md>
          <v-layout wrap>
            <v-flex xs12 session-nav>
              <v-btn fab dark small color="primary" xs12 :disabled="!hasPrevSession" @click="prevSession"
                  @shortkey="prevSession" v-shortkey="['arrowleft']">
                <v-icon dark>chevron_left</v-icon>
              </v-btn>
              <v-btn fab dark small color="primary" :disabled="!hasNextSession" @click="nextSession"
                  @shortkey="nextSession" v-shortkey="['arrowright']">
                <v-icon dark>chevron_right</v-icon>
              </v-btn>
              <span v-if="currentSession && currentSession.startTime" class="pl-1">{{ sessionName(currentSession) }}</span>
            </v-flex>

            <v-flex xs12>
              <v-data-table class="stats-table mb-4"
                :items="sessionInfo" hide-headers
                hide-actions disable-pagination>
                <template v-slot:items="props">
                  <tr>
                    <td class="text-xs-left name-column">{{ props.item.name }}</td>
                    <td class="text-xs-left value-column" >{{ props.item.value }}</td>
                  </tr>
                </template>
              </v-data-table>

              <i v-if="!chartSeries || chartSeries.length <= 0">No tasks were tracked for this session</i>
              <div id="chart" v-bind:style="{ display: (!chartSeries || chartSeries.length <= 0) ? 'none' : 'block'}">
                <apexchart type="donut" width="100%" height="100%" :options="chartOptions" :series="chartSeries"></apexchart>
              </div>
            </v-flex>
          </v-layout>
        </v-container>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
  import timeHelpers from '../timeHelpers';
  const VueApexCharts = require('vue-apexcharts');

  export default {
    name: 'App',
    components: {
      apexchart: VueApexCharts
    },
    props: [
      'currentProject', 
    ],
    data () {
      return {
        showDialog: false,

        currentSession: null,

        // Charts for stats modal
        chartSeries: [],
        chartOptions: {
          labels: [],
          responsive: [{
            breakpoint: 480,
            options: {
              chart: {
                width: 200
              },
              legend: {
                show: false
              }
            }
          }],
          legend: {
            position: 'right',
            offsetY: 0,
            height: 230
          }
        },
      }
    },
    created() {
      this.refreshCurrentSession();

      // https://apexcharts.com/docs/options/plotoptions/pie/
      this.chartOptions = {...this.chartOptions, ...{
        plotOptions: { pie: {
          donut: {
            labels: { 
              name: { show: false },
              show: true, value: { formatter: this.formatChartValue }
            }
          }
        }},
        tooltip: {
          enabled: true,
          y: { show: false, formatter: this.formatChartValue }
        }
      }};
    },
    mounted() {
      // Fix overflow (for tooltips)
      setTimeout(function() {
        var dialog = document.getElementById('charts-dialog');
        dialog.parentElement.style.overflow = "visible";
      }, 500);
    },
    computed: {
      projectSessions() {
        if (!this.currentProject) return null;
        return this.currentProject.sessions.filter(s => {
          return !!s.startTime;
        });
      },
      hasPrevSession() {
        let sessions = this.projectSessions;
        if (!sessions) return false;
        let index = sessions.findIndex(s => s.id == this.currentSession.id);
        return index > 0;
      },
      hasNextSession() {
        let sessions = this.projectSessions;
        if (!sessions) return false;
        let index = sessions.findIndex(s => s.id == this.currentSession.id);
        return index < sessions.length -1;
      },
      sessionInfo() {
        if (!this.currentProject || !this.currentSession) return [];
        else {
          let session = this.currentSession;
          let info = [];
          if (session.previousLapsed) {
            info.push({
              name: 'Duration', 
              value: this.formatTime(session.previousLapsed)
            });
          }
          if (session.targetTime && session.targetTime > 0 &&
               !isNaN(session.targetTime)) {
            info.push({
              name: 'Target duration', 
              value: this.formatTime(session.targetTime*3600000)
            });
          }
          if (session.objectives) {
            info.push({
              name: 'Objective(s)', 
              value: session.objectives
            });
          }
          if (session.endNotes) {
            info.push({
              name: 'Evaluation', 
              value: session.endNotes
            });
          }
          if (session.rating != null) {
            info.push({
              name: 'Rating', 
              value: session.rating + ' / 10'
            });
          }
          
          return info;
        }
      }
    },
    watch: {
      currentProject() {
        this.refreshCurrentSession();
      }
    },
    methods: {
      ...timeHelpers,

      openDialog() {
        this.showDialog = true;
      },
      updateChartSeries() {
        let self = this;
        let project = self.currentProject;
        let session = self.currentSession;
        if (!project || !session) self.chartSeries = [];
        else {
          let tasks = this.sessionTasks(session.id);
          self.chartOptions = {...self.chartOptions, ...{
            labels: tasks.map(t => t.name)
          }};
          self.chartSeries = tasks.map(t => {
            return self.getLapsedTimeTask(t)
          });
        }
      },
      formatChartValue(val) {
        return this.formatTime(val, false);
      },

      refreshCurrentSession() {
        let project = this.currentProject;
        let sessions = this.projectSessions;
        this.chartSeries = [];
        if (!project || !sessions) {
          this.currentSession = null;
        } else {
          this.currentSession = sessions.find(s => s.id == project.currentSessionId);
          if (!this.currentSession) {
            this.currentSession = sessions[sessions.length-1];
          }
        }
      },
      prevSession() {
        let sessions = this.projectSessions;
        if (!sessions || !this.currentSession) return;
        let index = sessions.findIndex(s => s.id == this.currentSession.id);
        if (index != 0) {
          this.currentSession = sessions[index-1];
          this.updateChartSeries();
        }
      },
      nextSession() {
        let sessions = this.projectSessions;
        if (!sessions || !this.currentSession) return;
        let index = sessions.findIndex(s => s.id == this.currentSession.id);
        if (index < sessions.length -1) {
          this.currentSession = sessions[index+1];
          this.updateChartSeries();
        }
      },

      sessionName(session) {
        return this.formatDate(session.startTime);
      },

      sessionTasks(sessionId) {
        let project = this.currentProject;
        if (!project) {
          return [];
        } else {
          return project.tasks.filter(t => t.sessionId == sessionId);
        }
      },
      getLapsedTimeTask(element) {
        let project = this.currentProject;
        let session = this.currentSession;
        if (!project || !session) return 0;
        var lapsed = (element.id == project.currentTaskId ? 
          (this.lapsedMillisecondsSession(session) - element.start) : 
          (element.stop - element.start)) + (element.previousLapsed || 0);
        if (lapsed < 0) lapsed = 0;
        return lapsed;
      },
      lapsedMillisecondsSession(session) {
        let project = this.currentProject;
        if (!project) return 0;
        else if (project.state == "reset") return 0;
        else if (project.state == "paused") {
          return session.previousLapsed || 0;
        } else {
          return Date.now() - session.startTime - session.pausedTime;
        }
      },
    }
  }
</script>

<style>
  .session-nav {
    margin-bottom: 20px;
  }
  .session-nav .v-btn {
    margin-left: 0;
    margin-right: 10px;
  }
  #chart {
    height: 400px;
  }
  .name-column {
    font-weight: bold !important;
    white-space: nowrap;
  }
  .value-column {
    width: 100%;
  }
  .stats-table {

  }
</style>